import { useEffect, useMemo, useRef, useState } from 'react'
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react'
import clsx from 'clsx'
import { useStore } from '@/lib/store'
import { useUI } from '@/lib/ui'
import { PLATFORMS } from '@/lib/platforms'
import type { Post } from '@/lib/types'

type View = 'day' | 'week' | 'month'
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const ROW_H = 56

function tintFor(color: string) {
  const c = color === '#ffffff' ? '#111111' : color
  return {
    glyph: c,
    bg: `color-mix(in srgb, ${c} 9%, #ffffff)`,
    border: `color-mix(in srgb, ${c} 28%, #e8e5dd)`,
    text: `color-mix(in srgb, ${c} 55%, #1b1a17)`,
  }
}

export function CalendarPage() {
  const channels = useStore((s) => s.channels)
  const posts = useStore((s) => s.posts)
  const weekStartsMonday = useStore((s) => s.settings.weekStartsMonday)
  const openComposer = useUI((s) => s.openComposer)
  const openConnect = useUI((s) => s.openConnect)
  const [view, setView] = useState<View>(() =>
    window.matchMedia('(max-width: 639px)').matches ? 'day' : 'week',
  )
  const [cursor, setCursor] = useState(() => {
    const nextScheduledPost = [...posts]
      .filter((post) => post.scheduledAt >= startOfDay(new Date()).getTime())
      .sort((left, right) => left.scheduledAt - right.scheduledAt)[0]
    return nextScheduledPost ? new Date(nextScheduledPost.scheduledAt) : new Date()
  })
  const [query, setQuery] = useState('')

  const weekOpts = { weekStartsOn: (weekStartsMonday ? 1 : 0) as 0 | 1 }

  const days = useMemo(() => {
    if (view === 'day') return [startOfDay(cursor)]
    if (view === 'week') {
      const start = startOfWeek(cursor, weekOpts)
      return Array.from({ length: 7 }, (_, i) => addDays(start, i))
    }
    const start = startOfWeek(startOfMonth(cursor), weekOpts)
    const end = endOfWeek(endOfMonth(cursor), weekOpts)
    const out: Date[] = []
    let d = start
    while (d <= end) {
      out.push(d)
      d = addDays(d, 1)
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursor, weekStartsMonday])

  const move = (dir: number) => {
    if (view === 'month') setCursor((c) => addMonths(c, dir))
    else setCursor((c) => addDays(c, dir * (view === 'week' ? 7 : 1)))
  }

  const range =
    view === 'month'
      ? format(cursor, 'MMMM yyyy')
      : view === 'day'
        ? format(cursor, 'EEEE, MMM d')
        : `${format(days[0], 'MMM d')} - ${format(days[days.length - 1], 'MMM d')}`

  const postsByDay = (d: Date) =>
    posts
      .filter((p) => isSameDay(p.scheduledAt, d))
      .filter((p) => !query.trim() || p.content.toLowerCase().includes(query.trim().toLowerCase()))
      .sort((a, b) => a.scheduledAt - b.scheduledAt)

  const visiblePosts = days.flatMap((day) => postsByDay(day))
  const activeDays = new Set(visiblePosts.map((post) => format(post.scheduledAt, 'yyyy-MM-dd'))).size

  const openSlot = (day: Date, hour: number) => {
    const dt = new Date(day)
    dt.setHours(hour, 0, 0, 0)
    openComposer({ scheduledAt: dt.getTime() })
  }

  return (
    <div className="flex h-full flex-col bg-page">
      <header className="border-b border-line bg-page">
        <div className="flex min-h-[68px] flex-wrap items-center gap-3 px-4 py-3 sm:px-5">
          <span className="grid size-8 shrink-0 place-items-center rounded-[8px] bg-surface text-ink-600 shadow-hairline">
            <CalendarDays size={16} />
          </span>
          <div>
            <h1 className="text-[15px] font-semibold tracking-[-0.015em] text-ink-900">Calendar</h1>
            <p className="mt-0.5 font-mono text-[10.5px] text-ink-400">{range}</p>
          </div>

          <div className="ml-2 inline-flex gap-0.5 rounded-control bg-field p-0.5">
          {(['day', 'week', 'month'] as View[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={clsx(
                'rounded-[6px] px-2.5 py-1 text-[11.5px] font-medium capitalize transition',
                view === v
                  ? 'bg-surface text-ink-900 shadow-btn'
                  : 'text-ink-600 hover:text-ink-900',
              )}
            >
              {v}
            </button>
          ))}
          </div>

          <div className="inline-flex items-center gap-1">
            <button onClick={() => move(-1)} className="grid size-7 place-items-center rounded-control bg-surface text-ink-600 shadow-btn transition hover:bg-hover hover:text-ink-900" aria-label="Previous period"><ChevronLeft size={13} /></button>
            <button onClick={() => setCursor(new Date())} className="h-7 rounded-control bg-surface px-2.5 text-[11.5px] font-medium text-ink-900 shadow-btn transition hover:bg-hover">Today</button>
            <button onClick={() => move(1)} className="grid size-7 place-items-center rounded-control bg-surface text-ink-600 shadow-btn transition hover:bg-hover hover:text-ink-900" aria-label="Next period"><ChevronRight size={13} /></button>
          </div>

          <div className="ml-auto hidden items-center gap-2 sm:flex">
            <label className="flex h-8 w-[180px] items-center gap-2 rounded-control bg-field px-2.5 shadow-hairline">
              <Search size={12} className="text-ink-400" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search schedule" className="min-w-0 flex-1 bg-transparent text-[11.5px] text-ink-900 placeholder:text-ink-400" />
            </label>
            <button onClick={() => openComposer()} className="btn-primary"><Plus size={13} /> New post</button>
          </div>
        </div>

        <div className="hidden grid-cols-3 divide-x divide-line border-t border-line bg-canvas lg:grid">
          <div className="px-5 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-[0.07em] text-ink-400">Visible posts</p>
            <p className="mt-0.5 font-mono text-[16px] font-medium tabular-nums text-ink-900">{visiblePosts.length}</p>
          </div>
          <div className="px-5 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-[0.07em] text-ink-400">Active days</p>
            <p className="mt-0.5 font-mono text-[16px] font-medium tabular-nums text-ink-900">{activeDays}/{days.length}</p>
          </div>
          <div className="px-5 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-[0.07em] text-ink-400">Open days</p>
            <p className="mt-1 text-[11.5px] text-ink-600">{days.length - activeDays > 0 ? `${days.length - activeDays} open day${days.length - activeDays === 1 ? '' : 's'} to plan` : 'The visible range is fully covered'}</p>
          </div>
        </div>
      </header>

      {channels.length === 0 ? (
        <EmptyChannels onConnect={openConnect} />
      ) : view === 'month' ? (
        <MonthGrid
          days={days}
          cursor={cursor}
          postsByDay={postsByDay}
          onDay={(d) => openComposer({ scheduledAt: noonOf(d) })}
          onPost={(p) => openComposer({ editId: p.id, ...p })}
        />
      ) : (
        <TimeGrid
          days={days}
          postsByDay={postsByDay}
          onSlot={openSlot}
          onPost={(p) => openComposer({ editId: p.id, ...p })}
        />
      )}
    </div>
  )
}

function noonOf(d: Date) {
  const x = new Date(d)
  x.setHours(12, 0, 0, 0)
  return x.getTime()
}

function EventChip({
  post,
  onClick,
  style,
}: {
  post: Post
  onClick: () => void
  style?: React.CSSProperties
}) {
  const channels = useStore((s) => s.channels)
  const primary = post.channelIds
    .map((id) => channels.find((c) => c.id === id))
    .find(Boolean)
  const platform = primary ? PLATFORMS[primary.platform] : null
  const tint = tintFor(platform?.color ?? '#2b49d8')
  const isDraft = post.status === 'draft'

  return (
    <button
      onClick={onClick}
      style={{
        ...style,
        background: tint.bg,
        borderColor: tint.border,
        color: tint.text,
        borderStyle: isDraft ? 'dashed' : 'solid',
      }}
      className="absolute left-1 right-1 z-10 flex flex-col gap-0.5 overflow-hidden rounded-[9px] border px-2 py-1.5 text-left shadow-card transition hover:z-20 hover:-translate-y-px hover:shadow-pop"
    >
      <div className="flex items-center gap-1.5">
        {platform && (
          <span
            className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded text-white"
            style={{ background: tint.glyph }}
          >
            <span className="inline-flex h-[9px] w-[9px]">{platform.icon}</span>
          </span>
        )}
        <span className="font-mono text-[9.5px] font-medium">
          {format(post.scheduledAt, 'h:mm a')}
        </span>
      </div>
      <span className={clsx('line-clamp-2 text-[11.5px] font-medium leading-tight', isDraft && 'italic')}>
        {post.content || '(media post)'}
      </span>
    </button>
  )
}

function TimeGrid({
  days,
  postsByDay,
  onSlot,
  onPost,
}: {
  days: Date[]
  postsByDay: (d: Date) => Post[]
  onSlot: (d: Date, h: number) => void
  onPost: (p: Post) => void
}) {
  const now = new Date()
  const gridCols = `58px repeat(${days.length}, 1fr)`
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = ROW_H * 8
  }, [])

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
      {/* sticky day header */}
      <div
        className="sticky top-0 z-30 grid border-b border-line bg-paper"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className="border-r border-line" />
        {days.map((d) => {
          const today = isSameDay(d, now)
          return (
            <div
              key={d.toISOString()}
              className={clsx(
                'border-r border-line px-2 py-2 text-center last:border-r-0',
                today && 'bg-brand-50/60',
              )}
            >
              <div
                className={clsx(
                  'font-mono text-[10px] uppercase tracking-[0.08em]',
                  today ? 'text-brand-500' : 'text-ink-400',
                )}
              >
                {format(d, 'EEE')}
              </div>
              <div
                className={clsx(
                  'mx-auto mt-0.5 grid h-7 min-w-[28px] place-items-center rounded-full text-[17px] font-semibold tabular-nums',
                  today ? 'bg-brand-500 text-white' : 'text-ink-900',
                )}
              >
                {format(d, 'd')}
              </div>
            </div>
          )
        })}
      </div>

      {/* hour grid */}
      <div className="relative grid" style={{ gridTemplateColumns: gridCols }}>
        {/* hour labels */}
        <div className="flex flex-col border-r border-line">
          {HOURS.map((h) => (
            <div
              key={h}
              className="relative pr-2 text-right font-mono text-[10px] text-ink-400"
              style={{ height: ROW_H }}
            >
              <span className="absolute -top-1.5 right-2">
                {h === 0 ? '' : format(new Date(2020, 0, 1, h), 'h a')}
              </span>
            </div>
          ))}
        </div>

        {days.map((d) => {
          const dayPosts = postsByDay(d)
          const today = isSameDay(d, now)
          return (
            <div
              key={d.toISOString()}
              className={clsx('relative border-r border-line last:border-r-0', today && 'bg-brand-50/40')}
            >
              {HOURS.map((h) => (
                <div
                  key={h}
                  onClick={() => onSlot(d, h)}
                  className="group cursor-pointer border-b border-line/60 transition hover:bg-brand-50/50"
                  style={{ height: ROW_H }}
                >
                  <span className="pointer-events-none flex h-full items-center justify-center text-ink-300 opacity-0 transition group-hover:opacity-100">
                    <Plus size={14} />
                  </span>
                </div>
              ))}

              {/* now line */}
              {today && (
                <div
                  className="pointer-events-none absolute left-0 right-0 z-20 flex items-center"
                  style={{ top: (now.getHours() + now.getMinutes() / 60) * ROW_H }}
                >
                  <span className="-ml-1 h-2 w-2 rounded-full bg-accent-red" />
                  <span className="h-0.5 flex-1 bg-accent-red" />
                </div>
              )}

              {/* posts */}
              {dayPosts.map((p) => {
                const dt = new Date(p.scheduledAt)
                const top = (dt.getHours() + dt.getMinutes() / 60) * ROW_H
                return (
                  <div key={p.id} onClick={(e) => e.stopPropagation()}>
                    <EventChip post={p} onClick={() => onPost(p)} style={{ top: top + 1, minHeight: 48 }} />
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MonthGrid({
  days,
  cursor,
  postsByDay,
  onDay,
  onPost,
}: {
  days: Date[]
  cursor: Date
  postsByDay: (d: Date) => Post[]
  onDay: (d: Date) => void
  onPost: (p: Post) => void
}) {
  const now = new Date()
  const weekdays = days.slice(0, 7)
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
      <div className="grid grid-cols-7">
        {weekdays.map((d) => (
          <div
            key={d.toISOString()}
            className="px-2 pb-2 text-center font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-ink-400"
          >
            {format(d, 'EEE')}
          </div>
        ))}
      </div>
      <div className="grid flex-1 auto-rows-fr grid-cols-7 gap-1.5">
        {days.map((d) => {
          const inMonth = isSameMonth(d, cursor)
          const today = isSameDay(d, now)
          const dayPosts = postsByDay(d)
          return (
            <div
              key={d.toISOString()}
              onClick={() => onDay(d)}
              className={clsx(
                'group min-h-[96px] cursor-pointer rounded-xl border border-line p-1.5 transition hover:border-brand-300 hover:bg-brand-50/40',
                inMonth ? 'bg-surface' : 'bg-panel opacity-60',
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={clsx(
                    'grid h-6 w-6 place-items-center rounded-full text-xs font-semibold tabular-nums',
                    today ? 'bg-brand-500 text-white' : 'text-ink-700',
                  )}
                >
                  {format(d, 'd')}
                </span>
                <Plus size={13} className="text-ink-300 opacity-0 transition group-hover:opacity-100" />
              </div>
              <div className="mt-1 space-y-1">
                {dayPosts.slice(0, 3).map((p) => (
                  <div key={p.id} onClick={(e) => e.stopPropagation()}>
                    <MonthChip post={p} onClick={() => onPost(p)} />
                  </div>
                ))}
                {dayPosts.length > 3 && (
                  <div className="px-1 font-mono text-[10px] text-ink-400">
                    +{dayPosts.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MonthChip({ post, onClick }: { post: Post; onClick: () => void }) {
  const channels = useStore((s) => s.channels)
  const primary = post.channelIds
    .map((id) => channels.find((c) => c.id === id))
    .find(Boolean)
  const platform = primary ? PLATFORMS[primary.platform] : null
  const tint = tintFor(platform?.color ?? '#2b49d8')
  return (
    <button
      onClick={onClick}
      style={{ background: tint.bg, borderColor: tint.border, color: tint.text }}
      className="flex w-full items-center gap-1 overflow-hidden rounded-md border px-1.5 py-1 text-left"
    >
      {platform && (
        <span
          className="grid h-3 w-3 shrink-0 place-items-center rounded-[3px] text-white"
          style={{ background: tint.glyph }}
        >
          <span className="inline-flex h-2 w-2">{platform.icon}</span>
        </span>
      )}
      <span className="font-mono text-[9px] font-medium">{format(post.scheduledAt, 'h:mm a')}</span>
      <span className="truncate text-[10px] font-medium">{post.content || '(media)'}</span>
    </button>
  )
}

function EmptyChannels({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="grid flex-1 place-items-center p-6">
      <div className="max-w-sm text-center">
        <span className="mx-auto mb-5 grid size-12 place-items-center rounded-xl bg-surface text-ink-500 shadow-hairline">
          <CalendarDays size={22} />
        </span>
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink-900">Connect a channel first</h2>
        <p className="mx-auto mt-2 max-w-xs text-sm text-ink-500">
          Connect a social account before you schedule work on the calendar.
        </p>
        <button
          onClick={onConnect}
          className="btn-primary mx-auto mt-5"
        >
          <Plus size={16} /> Connect a channel
        </button>
      </div>
    </div>
  )
}
